import React, { FC, useCallback, useContext } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { getCreateListingRoute } from 'utils';
import { useNavigate } from 'react-router-dom';
import { Button } from 'ncoded-component-library';
import { ListingType } from 'models/Listing';
import api from 'api';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import ClockIcon from 'icons/Clock.icon';
import ArrowrightIcon from 'icons/Arrowright.icon';
import { Direction } from 'models/Direction';
import ResolveImage from 'components/ResolveImage';
import useMatchMedia from 'hooks/useMatchMedia';
import BuyersImage from 'assets/images/GetBuyersReviews.webp';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';

import './GetBuyersBanner.styles.scss';
import './GetBuyersBanner.styles.responsive.scss';

type GetBuyersBannerProps = {
  className?: string;
  disabled?: boolean;
  hasImage: boolean;
  direction?: Direction;
  dealRoomId: string;
  listingId: string;
  listingType: ListingType;
};

const GetBuyersBanner: FC<GetBuyersBannerProps> = ({
  className,
  dealRoomId,
  disabled,
  listingId,
  listingType,
  hasImage,
  direction = 'row',
}) => {
  const classes = classNames(
    'get-buyers-banner',
    `get-buyers-banner--${direction}`,
    className,
  );

  const { t } = useTranslation();

  const navigate = useNavigate();

  const isBellowMobile = useMatchMedia('isBelowMobile');

  const { popServerError } = useContext(PopNotificationsContext);
  const { setCurrentUser } = useContext(CurrentUserContext);

  const handleOnSnooze = useCallback(() => {
    if (disabled) return;

    try {
      api.dealRoom.snoozeGetBuyers(dealRoomId);
      setCurrentUser((old) => ({
        ...old,
        snoozedDealRoomBannerNotification: true,
      }));
    } catch (e) {
      popServerError(e);
    }
  }, [dealRoomId, disabled, popServerError, setCurrentUser]);

  const handleGetBuyers = useCallback(() => {
    if (disabled) return;
    navigate(getCreateListingRoute(dealRoomId, listingId, listingType));
  }, [dealRoomId, disabled, listingId, listingType, navigate]);

  return (
    <div className={classes} onClick={(e) => e.stopPropagation()}>
      <div className="get-buyers-banner__description">
        <p>{t('closeFasterWithQualifiedBuyers')}</p>
        <p>{t('addNewLeadsWithProspecting')}</p>
      </div>

      {hasImage && <ResolveImage src={BuyersImage} asIllustration />}

      <div className="get-buyers-banner__actions">
        <Button
          className="confirm"
          iconPosition="right"
          disabled={disabled}
          icon={ArrowrightIcon}
          onClick={handleGetBuyers}
        >
          {t('getBuyers')}
        </Button>

        <Button
          variant="link"
          className="snooze"
          disabled={disabled}
          icon={!isBellowMobile ? ClockIcon : null}
          onClick={handleOnSnooze}
        >
          {t('remindMeLater')}
        </Button>
      </div>
    </div>
  );
};

export default GetBuyersBanner;
